import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';

import {
    useGetCouponRatesMutation,
    usePatchReservationMutation,
    usePostTrackEventMutation,
} from '@/api/rapidstor';
import { useExternalData } from '@/hooks/useExternalData';
import { formatCurrency } from '@/utils/currency';
import Logger from '@/utils/logging';
import {
    noAvailableUnitText,
    payLaterButtonText,
    payNowButtonText,
} from '@/utils/rapidstor';
import { createId } from '@paralleldrive/cuid2';

import store from '@/store';
import {
    selectContactData,
    setReservationData,
} from '@/store/bookingSlice';

import {
    descriptorTitle,
    formatTimeframe,
    sortUnitTypes,
} from '@/features/rapidstor/helpers';
import { reservationViaMoveInEvent } from '@/features/rapidstor/PostMessageEvents';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { MapContext } from '@/features/rapidstor/providers/MapProvider';
import ReservationService from '@/features/rapidstor/services/ReservationService';
import {
    DescriptorOptions,
    unitOptionsTransformer,
} from '@/features/rapidstor/UnitOptions';

import classes from './styles.module.scss';

import Button from '../elements/button';
import Spinner from '../elements/spinner';
import { CouponAlert } from '../rapidstor/coupon';
import Option from '../unit-option';

interface Props {
    descriptor: Descriptor;
    unitType: UnitType;
    deals: Deal[];
    selectedUnitType: string;
    setSelectedUnitType: React.Dispatch<React.SetStateAction<string>>;
    selectedIndex: number;
    selectedDeal: string | null;
    setSelectedDeal: React.Dispatch<React.SetStateAction<string | null>>;
    changeDescriptorSize: (newIndex: number) => UnitType | null;
    hideSmallerButton: boolean;
    hideLargerButton: boolean;
}

export default function BookingCard({
    descriptor,
    unitType,
    deals,
    selectedUnitType,
    setSelectedUnitType,
    changeDescriptorSize,
    selectedIndex,
    selectedDeal,
    setSelectedDeal
}: Props) {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const contactData = useSelector(selectContactData);

    const { 
        unitData, 
        reservationData, 
        persona, 
        toggleContactCard,
        handleMakeReservation,
    } = useContext(JourneyContext);
    const { generateExternalData } = useExternalData();
    const { setFirstAvailableUnit } = useContext(MapContext);

    const [cookies] = useCookies(['contactData', 'sessionId']);

    const [getCouponRates] = useGetCouponRatesMutation();
    const [patchReservation] = usePatchReservationMutation();
    const [postTrackEvent] = usePostTrackEventMutation();

    const [code, setCode] = useState<string>('');
    const [couponMessage, setCouponMessage] = useState<CouponAlert | null>(null);
    const [couponLoading, setCouponLoading] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [couponDeal, setCouponDeal] = useState<Deal | null>(null);

    const { t } = useTranslation();

    const useDescriptorOptions = useMemo(() => {
        return DescriptorOptions(descriptor, unitData?.descriptors ?? [], location);
    }, [descriptor, unitData?.descriptors, location]);

    const displayRate = useMemo(() => {
        return formatCurrency(unitType.rates.rate, location);
    }, [location, unitType]);

    const interimSelectedUnitType = useMemo(() => {
        const allUnitTypes = unitData?.descriptors.map((d) => d.unitTypes).flat();
        const unitTypeToBook = allUnitTypes?.find((ut) => ut._id === selectedUnitType);

        return unitTypeToBook || null;
    }, [selectedUnitType, unitData?.descriptors]);

    const descriptorOptions = () => {
        return useDescriptorOptions.getDescriptorUpsells(unitData?.descriptors ?? []);
    };

    const unitTypeOptions = () => {
        return useDescriptorOptions
            .getUnitTypeUpsells()
            .filter((option) => option._id !== unitType._id);
    };

    const dealDiscountAmount = (deal: Deal) => {
        return (
            formatCurrency(
                deal?.rates?.discountSaved ?? 0,
                location,
                false,
                true
            ) + ' off'
        );
    };

    const discountAmount = (coupon?: boolean) => {

        if (coupon && couponDeal) {
            return (
                formatCurrency(
                    couponDeal.rates.rate -
                    (couponDeal.rates.discountedRate ?? 0),
                    location,
                    false,
                    true
                ) + ' off'
            );
        }

        return (
            formatCurrency(
                (interimSelectedUnitType || unitType).rates.rate -
                ((interimSelectedUnitType || unitType).rates.discountedRate ?? 0),
                location,
                false,
                true
            ) + ' off'
        );
    };

    const postCouponApply = async () => {
        if (!code) {
            setCouponMessage(null);
            return;
        }

        setCouponLoading(true);
        try {
            const result = await getCouponRates({
                couponCode: code,
                unitTypeId: unitType._id,
                corpCode: location.corpCode,
                locationCode: location.locationCode,
            }).unwrap();

            let deal = result.deal;
            deal = {
                ...deal,
                rates: {
                    ...result.rates,
                },
            };

            setCouponDeal(deal);
            setSelectedDeal(deal._id);

            setCouponMessage({
                text: 'Coupon applied successfully.',
                type: 'success',
            });
        } catch (error) {
            setCouponMessage({
                text: 'Failed to apply coupon code.',
                type: 'error',
            });
        }
        setCouponLoading(false);
    };

    const bookNow = async () => {
        if (!unitData) {
            return;
        }
        setIsLoading(true);
        const reservationSvc = new ReservationService(location);
        const neededDateObject = contactData?.neededDate
            ? new Date(contactData.neededDate)
            : cookies?.contactData?.neededDate
                ? new Date(cookies.contactData.neededDate)
                : new Date();
        const neededDate = neededDateObject.toISOString();

        const allUnitTypes = unitData?.descriptors.map((d) => d.unitTypes).flat();
        let descriptorToBook: Descriptor | null = null;
        for (const descriptor of unitData.descriptors) {
            if (descriptor.unitTypes.find((ut) => ut._id === selectedUnitType)) {
                descriptorToBook = descriptor;
                break;
            }
        }
        const unitTypeToBook = allUnitTypes?.find((ut) => ut._id === selectedUnitType);

        if (!unitTypeToBook || !descriptorToBook) {
            setIsLoading(false);
            return;
        }

        // If token exists, patch the current reservation otherwise create a new one
        if (reservationData?.token) {
            try {
                const patchPayload = reservationSvc.buildPatchPayload(
                    reservationData.token,
                    neededDate,
                    unitTypeToBook._id,
                    selectedDeal,
                    {},
                    {}
                );
                await patchReservation(patchPayload).unwrap();

                Logger.info(`[BookingCard] Reservation patched successfully`, location);
            } catch (err) {
                reservationSvc.handleError(
                    `[BookingCard] Reservation patch failed`,
                    JSON.stringify(err)
                );
                setIsLoading(false);
            }

            navigate(`/move-in/${reservationData.token}`);
        } else {
            try {
                const contactDataToUse = contactData || cookies.contactData;
                const reservationDataFresh = {
                    ...reservationData,
                    descriptor: descriptorToBook,
                    unitType: unitTypeToBook,
                    unitToUse: unitTypeToBook.units.find((u) => u.sUnitName === unitTypeToBook.firstAvailableUnitName),
                    deal: selectedDeal ?? undefined,
                };

                const payload = reservationSvc.buildPostPayload(
                    reservationDataFresh,
                    false,
                    false,
                    contactDataToUse,
                    neededDate,
                    generateExternalData()
                );

                if (persona) {
                    payload.payload.persona = persona;
                }

                const result = await handleMakeReservation(payload, descriptorToBook._id);
                if (!result) {
                    setIsLoading(false);
                    return;
                }

                Logger.info(`[BookingCard] Reservation created successfully`, location);

                store.dispatch(
                    setReservationData({
                        ...reservationData,
                        descriptor: descriptorToBook,
                        unitType: unitTypeToBook,
                        token: result.token,
                    })
                );

                const eventPayload = {
                    site_info: {
                        location_code: location.locationCode,
                        site_token: location.token,
                    },
                } as ReservationEventPayloadStandard;

                reservationViaMoveInEvent(eventPayload);
                sendReservationTrackingEvent();

                navigate(`/move-in/${result.token}`);
            } catch (err) {
                reservationSvc.handleError(
                    `[BookingCard] Error thrown in POST reservation request`,
                    JSON.stringify(err)
                );
                setIsLoading(false);
            }
        }
    };

    const sendReservationTrackingEvent = () => {
        try {
            postTrackEvent({
                locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                payload: {
                    trackingId: createId(),
                    url: window.location.href,
                    referrerUrl: document.referrer,
                    userAgent: window.navigator.userAgent,
                    action: 'reservation',
                    timestamp: new Date().toISOString(),
                },
            });
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in POST track event request: ${JSON.stringify(
                    error
                )}`,
                location
            );
        }
    };

    const clearCouponData = async (unitTypeId: string) => {
        if (!code) {
            return;
        }
        setCouponLoading(true);

        try {
            const result = await getCouponRates({
                couponCode: code,
                unitTypeId: unitTypeId,
                corpCode: location.corpCode,
                locationCode: location.locationCode,
            }).unwrap();

            let deal = result.deal;
            deal = {
                ...deal,
                rates: {
                    ...result.rates,
                },
            };

            setCouponDeal(deal);
            setSelectedDeal(deal._id);

            setCouponMessage({
                text: 'Coupon applied successfully.',
                type: 'success',
            });
        } catch (error) {
            setCouponMessage({
                text: 'Failed to clear coupon code.',
                type: 'error',
            });
        }

        setCouponLoading(false);
    }

    const descriptorTotalAvailable = (descriptor: Descriptor) => {
        const result = descriptor.unitTypes.reduce((acc, unitType) => {
            if (!unitType.units.length) {
                return acc;
            }
            return acc + unitType.totalAvailable;
        }, 0);

        return result;
    };

    useEffect(() => {
        if (useDescriptorOptions.isDescriptorUpsell()) {
            const unitType = sortUnitTypes(descriptor.unitTypes, location)[0];
            setFirstAvailableUnit(unitType.firstAvailableUnitName);
        } else {
            const uT = descriptor.unitTypes.filter((u) => u.totalAvailable >= 1)[0];
            if (uT) {
                setFirstAvailableUnit(uT.firstAvailableUnitName);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [descriptor]);

    return (
        <div className={`${classes.descriptorCard}`}>
            <div className={`${classes.topSection}`}>
                <div className={`${classes.header}`}>
                    <span className={`${classes.booking_details}`}>{t('map:booking_card:title')}</span>
                </div>

                <div className={`${classes.buttons}`}>
                    <Button
                        size='small'
                        type={'primary'}
                        onclick={() => {
                            const unitType = changeDescriptorSize(selectedIndex - 1)
                            if (unitType) {
                                clearCouponData(unitType._id);
                            }
                        }}>{t('map:smaller')}</Button>
                    <Button
                        size='small'
                        type={'primary'}
                        onclick={() => {
                            const unitType = changeDescriptorSize(selectedIndex + 1)
                            if (unitType) {
                                clearCouponData(unitType._id);
                            }
                        }}>{t('map:bigger')}</Button>
                </div>

                <div className={`${classes.first}`}>
                    <div className={`${classes.opt_in}`}>
                        <div className={`${classes.custom_checkbox}`}>
                            <input
                                id={unitType._id}
                                type='checkbox'
                                checked={selectedUnitType === unitType._id ? true : false}
                                onChange={() => {
                                    setSelectedUnitType(unitType._id);
                                    clearCouponData(unitType._id);
                                    setFirstAvailableUnit(
                                        unitType.firstAvailableUnitName
                                    );
                                }}
                            />
                            <label
                                htmlFor={unitType._id}
                                className={`${classes.label}`}
                            ></label>
                        </div>
                        <div className={`${classes.upsell_description}`}>
                            <span className={`${classes.name}`}>
                                {descriptorTitle(descriptor, location)[1]}
                            </span>
                        </div>
                    </div>
                    <div className={`${classes.pricing}`}>
                        <span className={`${classes.timeframe}`}>
                            {formatTimeframe(location)}
                        </span>
                        <span className={`${classes.price}`}>{displayRate}</span>
                    </div>
                </div>

                {/* <div className={`${classes.addons}`}>
                    <span className={`${classes.header}`}>
                        ADD-ON AND UNIT CUSTOMISATION
                    </span>
                    <span className={`${classes.optional_tag}`}>Optional</span>
                </div>

                <span className={`${classes.description}`}>{descriptor.description}</span> */}

                {useDescriptorOptions.isDescriptorUpsell() ? (
                    <>
                        {descriptorOptions().map((option) => {
                            const formattedUnitType = {
                                ...unitOptionsTransformer(option)[0],
                            };
                            formattedUnitType.typeName = `${option.upgradeReason}`;
                            return (
                                <Option
                                    key={`${formattedUnitType._id}_upsell_options_idx`}
                                    location={location}
                                    option={formattedUnitType}
                                    deal={formattedUnitType.deal?._id as string}
                                    selected={selectedUnitType === formattedUnitType._id}
                                    theme={'map'}
                                    setSelectedID={() => {
                                        clearCouponData(formattedUnitType._id);
                                        setFirstAvailableUnit(
                                            formattedUnitType.firstAvailableUnitName
                                        );
                                        setSelectedUnitType(formattedUnitType._id);
                                    }}
                                    setDefaultUnitType={() => {
                                        clearCouponData(unitType._id);
                                        setFirstAvailableUnit(
                                            unitType.firstAvailableUnitName
                                        );
                                        setSelectedUnitType(unitType._id);
                                    }}
                                    initialRate={unitType.rates.rate}
                                />
                            );
                        })}
                    </>
                ) : (
                    <>
                        {unitTypeOptions().map((option) => (
                            <Option
                                key={`${option._id}_upsell_options_idx`}
                                location={location}
                                option={option}
                                deal={option.deal?._id as string}
                                selected={selectedUnitType === option._id}
                                theme={'map'}
                                setSelectedID={() => {
                                    clearCouponData(option._id);
                                    setSelectedUnitType(option._id);
                                    setFirstAvailableUnit(option.firstAvailableUnitName);
                                }}
                                setDefaultUnitType={() => {
                                    clearCouponData(unitType._id);
                                    setSelectedUnitType(unitType._id);
                                    setFirstAvailableUnit(unitType.firstAvailableUnitName);
                                }}
                                initialRate={unitType.rates.rate}
                            />
                        ))}
                    </>
                )}
            </div>

            <div className={`${classes.deals_header}`}>
                <span className={`${classes.header}`}>
                    {t('map:booking_card:deals')}
                </span>

                {couponMessage && (
                    <div
                        className={`${classes.coupon_message} ${couponMessage.type === 'error'
                            ? classes.error
                            : classes.success
                            }`}
                    >
                        <span>{couponMessage.text}</span>
                    </div>
                )}

                <div className={`${classes.sub_header}`}>
                    <span className={`${classes.copy}`}>
                        {t('map:booking_card:select_a_deal')}
                    </span>
                    <div className={`${classes.coupon}`}>
                        <input
                            placeholder='Coupon'
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            disabled={couponLoading}
                        />
                        <button disabled={couponLoading} onClick={postCouponApply}>
                            <svg
                                width='15'
                                height='16'
                                viewBox='0 0 15 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M1.99219 7.99998C1.99219 7.90674 2.02923 7.81732 2.09516 7.75139C2.16109 7.68546 2.25051 7.64842 2.34376 7.64842L11.8072 7.64842L8.18907 4.02967C8.15453 3.99748 8.12682 3.95867 8.10761 3.91554C8.08839 3.87242 8.07806 3.82587 8.07723 3.77866C8.07639 3.73146 8.08508 3.68457 8.10276 3.64079C8.12044 3.59702 8.14676 3.55725 8.18014 3.52387C8.21353 3.49048 8.25329 3.46417 8.29707 3.44648C8.34084 3.4288 8.38773 3.42012 8.43494 3.42095C8.48214 3.42178 8.52869 3.43212 8.57182 3.45133C8.61494 3.47055 8.65376 3.49825 8.68594 3.53279L12.9047 7.75154C12.9705 7.81746 13.0075 7.90682 13.0075 7.99998C13.0075 8.09314 12.9705 8.1825 12.9047 8.24842L8.68594 12.4672C8.6193 12.5293 8.53115 12.5631 8.44007 12.5615C8.34899 12.5599 8.26209 12.523 8.19768 12.4586C8.13327 12.3941 8.09637 12.3072 8.09477 12.2162C8.09316 12.1251 8.12697 12.0369 8.18907 11.9703L11.8072 8.35154L2.34376 8.35154C2.25051 8.35154 2.16109 8.3145 2.09516 8.24857C2.02923 8.18264 1.99219 8.09322 1.99219 7.99998Z'
                                    fill='black'
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <motion.div
                className={`${classes.deals_section}`}
                layout={false}
                animate={{
                    height: (deals.length > 0 || couponDeal) ? 'auto' : 0,
                    opacity: (deals.length > 0 || couponDeal) ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
            >
                <motion.div
                    layout={false}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: (deals.length > 0 || couponDeal) ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className={`${classes.deals_body}`}
                >
                    {(couponDeal && !(deals.map(deal => deal._id).includes(couponDeal._id))) && (
                        <div
                            key={`${couponDeal._id}`}
                            className={`${classes.deal_card} ${selectedDeal === couponDeal._id && classes.active
                                }`}
                            onClick={() => {
                                if (selectedDeal === couponDeal._id) {
                                    setSelectedDeal(null);
                                } else {
                                    setSelectedDeal(`${couponDeal._id}`);
                                }
                            }}
                        >
                            <div className={`${classes.top_section}`}>
                                <div className={`${classes.opt_in}`}>
                                    <div className={`${classes.custom_checkbox}`}>
                                        <input
                                            id={`${couponDeal._id}`}
                                            type='checkbox'
                                            checked={couponDeal._id === selectedDeal}
                                            disabled
                                        />
                                        <label
                                            htmlFor={`${couponDeal._id}`}
                                            className={`${classes.label}`}
                                        ></label>
                                    </div>
                                    <div className={`${classes.copy}`}>
                                        <span>{couponDeal.title}</span>
                                        {couponLoading ? (
                                            <span><ContentLoader
                                                speed={1}
                                                width={'100%'}
                                                height={24}
                                                viewBox='0 0 100 20'
                                                backgroundColor='#fefefe'
                                                foregroundColor='#D3D3D3'
                                            >
                                                <rect x='0' y='0' rx='5' ry='5' width='100' height='20' />
                                            </ContentLoader></span>
                                        ) : (
                                            <span>{discountAmount(true)}</span>
                                        )}
                                    </div>
                                </div>

                                <div className={`${classes.pricing}`}>
                                    <span className={`${classes.timeframe}`}>
                                        {formatTimeframe(location)}
                                    </span>
                                    <span className={`${classes.price}`}>
                                        {couponLoading ? (
                                            <ContentLoader
                                                speed={1}
                                                width={'100%'}
                                                height={24}
                                                viewBox='0 0 100 20'
                                                backgroundColor='#fefefe'
                                                foregroundColor='#D3D3D3'
                                            >
                                                <rect x='0' y='0' rx='5' ry='5' width='100' height='20' />
                                            </ContentLoader>
                                        ) : (
                                            <>{formatCurrency(
                                                couponDeal.rates.discountedRate ?? 0,
                                                location
                                            )}</>
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className={`${classes.tags}`}>
                                {couponDeal.descriptions.map((description, index) => (
                                    <span
                                        key={`${index}_${couponDeal._id}_description_idx`}
                                        className={`${classes.deal_description_tag}`}
                                    >
                                        {description}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {deals.map((deal) => (
                        <div
                            key={`${deal._id}`}
                            className={`${classes.deal_card} ${selectedDeal === deal._id && classes.active
                                }`}
                            onClick={() => {
                                if (selectedDeal === deal._id) {
                                    setSelectedDeal(null);
                                } else {
                                    setSelectedDeal(`${deal._id}`);
                                }
                            }}
                        >
                            <div className={`${classes.top_section}`}>
                                <div className={`${classes.opt_in}`}>
                                    <div className={`${classes.custom_checkbox}`}>
                                        <input
                                            id={`${deal._id}`}
                                            type='checkbox'
                                            checked={deal._id === selectedDeal}
                                            disabled
                                        />
                                        <label
                                            htmlFor={`${deal._id}`}
                                            className={`${classes.label}`}
                                        ></label>
                                    </div>
                                    <div className={`${classes.copy}`}>
                                        <span>{deal.title}</span>
                                        <span>{dealDiscountAmount(deal)}</span>
                                    </div>
                                </div>

                                <div className={`${classes.pricing}`}>
                                    <span className={`${classes.timeframe}`}>
                                        {formatTimeframe(location)}
                                    </span>
                                    <span className={`${classes.price}`}>
                                        {formatCurrency(
                                            deal.rates.discountedRate ?? 0,
                                            location
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className={`${classes.tags}`}>
                                {deal.descriptions.map((description, index) => (
                                    <span
                                        key={`${index}_${deal._id}_description_idx`}
                                        className={`${classes.deal_description_tag}`}
                                    >
                                        {description}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            <div className={`${classes.bottom_section}`}>
                <span className={`${classes.size_text}`}>
                    <span>{t('map:booking_card:different_unit')}</span>
                </span>
                <div className={`${classes.buttons}`}>
                    <div>
                        <Button
                            onclick={() => {
                                toggleContactCard('reservation', unitType);
                            }}
                            type={`blackOnWhite`}
                            size={`medium`}
                        >
                            {payLaterButtonText(location)}
                        </Button>
                    </div>
                    <div>
                        <Button
                            onclick={bookNow}
                            disabled={isLoading || unitType.totalAvailable <= 0}
                            type={`primary`}
                            size={`medium`}
                        >
                            {descriptorTotalAvailable(descriptor) >= 1 ? (
                                <>
                                    {isLoading ? <Spinner /> : payNowButtonText(location)}
                                </>
                            ) : (
                                <>{noAvailableUnitText(location)}</>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div >
    );
}
